import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Box, Typography, Input, Select, Option, Button, Divider, Sheet, IconButton } from '@mui/joy';

/** genera id stabile */
const makeId = () => (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

/** assicura _id senza cambiare il resto */
const ensureId = (item) => (item?._id ? item : { ...item, _id: makeId() });

/** rinumera gli ID delle categorie in base all'ordine corrente */
const normalizeModuleIds = (list = []) =>
  list.map((item, index) => ({
    ...item,
    id: index + 1,
  }));

/** RIGA TRASCINABILE */
function SortableSectionRow({ id, canEdit, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          width: 28,
          height: 28,
          borderRadius: 'sm',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: canEdit ? 'grab' : 'default',
          userSelect: 'none',
          border: '1px solid',
          borderColor: 'neutral.outlinedBorder',
          bgcolor: 'background.surface',
          flexShrink: 0,
        }}
        title={canEdit ? 'Trascina per riordinare' : undefined}
      >
        ⋮⋮
      </Box>

      <Box sx={{ flex: 1, display: 'flex', gap: 2, alignItems: 'center' }}>{children}</Box>
    </Box>
  );
}

export default function CategorySectionsEditor({ sections, setSections, canEdit }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  React.useEffect(() => {
    setSections((prev) =>
      normalizeModuleIds(
        (prev ?? []).map((mod) => ({
          ...mod,
          ownership: mod.ownership ?? 1,
          all: mod.all ?? 0,
          section: (mod.section ?? []).map(ensureId),
        }))
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleModuleChange = (index, field, value) => {
    if (!canEdit) return;
    setSections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSectionChange = (moduleIndex, sectionIndex, field, value) => {
    if (!canEdit) return;

    setSections((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      const list = [...(mod.section ?? [])];

      const row = { ...list[sectionIndex] };

      if (mod.name === 'EQUIPAGGIO') {
        row.type = 'input';
        row.qt = '1';
        if (field === 'value') row.value = value;
      } else {
        row[field] = value;
      }

      list[sectionIndex] = row;
      next[moduleIndex] = { ...mod, section: list };
      return next;
    });
  };

  const addSectionRow = (moduleIndex) => {
    if (!canEdit) return;

    setSections((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      const isEquipaggio = mod.name === 'EQUIPAGGIO';

      const newRow = ensureId({
        value: '',
        type: 'input',
        qt: isEquipaggio ? '1' : '',
      });

      next[moduleIndex] = { ...mod, section: [...(mod.section ?? []), newRow] };
      return next;
    });
  };

  const removeSectionRow = (moduleIndex, sectionIndex) => {
    if (!canEdit) return;

    setSections((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      const list = [...(mod.section ?? [])];
      list.splice(sectionIndex, 1);
      next[moduleIndex] = { ...mod, section: list };
      return next;
    });
  };

  const moveModuleRow = (fromIndex, toIndex) => {
    if (!canEdit) return;

    setSections((prev) => {
      if (toIndex < 0 || toIndex >= prev.length) return prev;
      return normalizeModuleIds(arrayMove([...prev], fromIndex, toIndex));
    });
  };

  const onDragEndForModule = (moduleIndex) => ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;

    setSections((prev) => {
      const next = [...prev];
      const mod = next[moduleIndex];
      const list = [...(mod.section ?? [])];

      const oldIndex = list.findIndex((x) => x._id === active.id);
      const newIndex = list.findIndex((x) => x._id === over.id);

      if (oldIndex === -1 || newIndex === -1) return prev;

      next[moduleIndex] = { ...mod, section: arrayMove(list, oldIndex, newIndex) };
      return next;
    });
  };

  const statusOptions = [
    { value: true, label: 'Attiva' },
    { value: false, label: 'Inattiva' },
  ];

  const ownershipOptions = [
    { value: 1, label: 'Tutti' },
    { value: 2, label: 'Dipendenti' },
  ];

  const allOptions = [
    { value: 0, label: 'No seleziona tutto' },
    { value: 1, label: 'Seleziona tutto' },
  ];

  const countCheckFields = (mod) => {
    return (mod.section ?? []).filter((item) => item?.type === 'check').length;
  };

  const showAllSelect = (mod) => countCheckFields(mod) >= 2;

  return (
    <Box display="flex" flexDirection="column" gap={4}>
      {(sections ?? []).map((mod, i) => {
        const isEquipaggioModule = mod.name === 'EQUIPAGGIO';
        const rows = mod.section ?? [];

        return (
          <Sheet
            key={mod.id ?? mod.name ?? i}
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 'lg',
              ...(mod.status === false && {
                bgcolor: 'warning.softBg',
                borderColor: 'warning.outlinedBorder',
              }),
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              gap={2}
              flexWrap="wrap"
            >
              <Box display="flex" alignItems="center" gap={2}>
                <Typography
                  level="body-sm"
                  sx={{
                    minWidth: 36,
                    height: 36,
                    borderRadius: 'sm',
                    border: '1px solid',
                    borderColor: 'neutral.outlinedBorder',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {mod.id}
                </Typography>

                <img
                  src={`${process.env.REACT_APP_SERVER_HOST}/assets/img/icon/${mod.icon}.png`}
                  alt="icon"
                  style={{ width: 40, height: 30 }}
                />

                <Box display="flex" alignItems="center" gap={1}>
                  <Typography level="title-md">{mod.name}</Typography>
                  {!mod.status && (
                    <Typography level="body-xs" color="warning">
                      (inattiva)
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {canEdit && (
                  <>
                    <IconButton
                      size="sm"
                      variant="soft"
                      disabled={i === 0}
                      onClick={() => moveModuleRow(i, i - 1)}
                    >
                      ↑
                    </IconButton>

                    <IconButton
                      size="sm"
                      variant="soft"
                      disabled={i === (sections ?? []).length - 1}
                      onClick={() => moveModuleRow(i, i + 1)}
                    >
                      ↓
                    </IconButton>
                  </>
                )}

                <Select
                  size="sm"
                  value={mod.status}
                  disabled={!canEdit}
                  onChange={(_, value) => handleModuleChange(i, 'status', value)}
                >
                  {statusOptions.map((opt) => (
                    <Option key={opt.value.toString()} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>

                {showAllSelect(mod) && (
                  <Select
                    size="sm"
                    value={mod.all ?? 0}
                    disabled={!canEdit}
                    onChange={(_, value) => handleModuleChange(i, 'all', value)}
                  >
                    {allOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                )}

                <Select
                  size="sm"
                  value={mod.ownership ?? 1}
                  disabled={!canEdit}
                  onChange={(_, value) => handleModuleChange(i, 'ownership', value)}
                >
                  {ownershipOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEndForModule(i)}
            >
              <SortableContext items={rows.map((r) => r._id)} strategy={verticalListSortingStrategy}>
                {rows.map((item, j) => {
                  const viewItem = isEquipaggioModule
                    ? { ...item, type: 'input', qt: '1' }
                    : item;

                  return (
                    <SortableSectionRow key={item._id} id={item._id} canEdit={canEdit}>
                      <Input
                        placeholder="Descrizione"
                        value={viewItem.value}
                        disabled={!canEdit}
                        onChange={(e) => handleSectionChange(i, j, 'value', e.target.value)}
                        sx={{ flex: 2 }}
                      />

                      {isEquipaggioModule ? (
                        <>
                          <Select value="input" disabled sx={{ width: 120 }}>
                            <Option value="input">Input</Option>
                          </Select>
                          <Input type="number" value="1" disabled sx={{ width: 100 }} />
                        </>
                      ) : (
                        <>
                          <Select
                            value={viewItem.type}
                            disabled={!canEdit}
                            onChange={(_, val) => handleSectionChange(i, j, 'type', val)}
                            sx={{ width: 120 }}
                          >
                            <Option value="check">Check</Option>
                            <Option value="input">Input</Option>
                            <Option value="photo">Photo</Option>
                          </Select>

                          <Box sx={{ width: 100 }}>
                            {viewItem.type !== 'photo' ? (
                              <Input
                                type="number"
                                placeholder="Valore"
                                value={viewItem.qt}
                                disabled={!canEdit}
                                onChange={(e) => handleSectionChange(i, j, 'qt', e.target.value)}
                                fullWidth
                              />
                            ) : (
                              <Box sx={{ height: '40px' }} />
                            )}
                          </Box>
                        </>
                      )}

                      {canEdit && (
                        <Button
                          size="sm"
                          variant="soft"
                          color="danger"
                          onClick={() => removeSectionRow(i, j)}
                        >
                          -
                        </Button>
                      )}
                    </SortableSectionRow>
                  );
                })}
              </SortableContext>
            </DndContext>

            {canEdit && (
              <Button size="sm" onClick={() => addSectionRow(i)} sx={{ mt: 1 }}>
                + Aggiungi riga
              </Button>
            )}
          </Sheet>
        );
      })}
    </Box>
  );
}